/**
 * Kainų įžvalgų skaičiavimo logika pagal Nord Pool kainas
 * 
 * Reikalavimai:
 * 1. 168h (7 d.) slenkantis langas statistikai (P25, P50, P75)
 * 2. Dienos reitingas (Daily Rank) nuo 1 iki 24
 * 3. Klasifikavimas (Kritiškai pigu, Pigu, Normalu, Brangu, Kritiškai brangu)
 * 4. Krizės taisyklė (min 3 val. "Normalu")
 */

export function calculatePriceInsights(currentDayPrices, historicalPrices) {
  if (!currentDayPrices || currentDayPrices.length === 0) return [];

  // 1. Paruošiame istorinius duomenis (168h langas)
  const windowPrices = historicalPrices.map(p => p.price).sort((a, b) => a - b);
  const p25 = getPercentile(windowPrices, 0.25);
  const p50 = getPercentile(windowPrices, 0.50);
  const p75 = getPercentile(windowPrices, 0.75);

  // 2. Apskaičiuojame Daily Rank valandinėms kainoms
  // Kadangi turime 15 min duomenis, sugrupuojame į valandas analizėms
  const hourlyData = groupByHour(currentDayPrices);
  const sortedHourlyPrices = [...hourlyData].sort((a, b) => a.price - b.price);
  
  const dailyRanks = {};
  sortedHourlyPrices.forEach((item, index) => {
    dailyRanks[item.hour] = index + 1; // Rank 1-24
  });

  // 3. Patikriname krizės taisyklę (ar visa para > P75)
  const isCrisis = hourlyData.every(h => h.price > p75);
  const cheapestHoursInCrisis = isCrisis 
    ? sortedHourlyPrices.slice(0, 3).map(h => h.hour)
    : [];

  // 4. Klasifikuojame kiekvieną valandą
  const insights = hourlyData.map(h => {
    const price = h.price;
    const rank = dailyRanks[h.hour];
    let category = 'NORMAL';
    let label = 'Normalu';
    let color = '#94a3b8'; // Pilka (var(--text-muted) atitikmuo)
    let cssClass = 'cat-normal';

    // A. KRITIŠKAI PIGU: (Kaina <= 0) ARBA (Kaina < P25 IR Daily_Rank <= 6)
    if (price <= 0 || (price < p25 && rank <= 6)) {
      category = 'CRITICAL_CHEAP';
      label = 'Kritiškai pigu';
      color = '#10b981'; // Žalia
      cssClass = 'cat-critical-cheap';
    }
    // B. PIGU: (Kaina < P50 IR Daily_Rank <= 10)
    else if (price < p50 && rank <= 10) {
      category = 'CHEAP';
      label = 'Pigu';
      color = '#60a5fa'; // Mėlyna
      cssClass = 'cat-cheap';
    }
    // E. KRITIŠKAI BRANGU: (Kaina > P75 IR Daily_Rank >= 19)
    else if (price > p75 && rank >= 19) {
      category = 'CRITICAL_EXPENSIVE';
      label = 'Kritiškai brangu';
      color = '#ef4444'; // Raudona
      cssClass = 'cat-critical-expensive';
    }
    // D. BRANGU: (Daily_Rank > 14) - jei pateko į top 10 brangiausių, žymime bent kaip "Brangu"
    else if (rank > 14) {
      category = 'EXPENSIVE';
      label = 'Brangu';
      color = '#fbbf24'; // Geltona/Oranžinė
      cssClass = 'cat-expensive';
    }
    // C. NORMALU (default)
    else {
      category = 'NORMAL';
      label = 'Normalu';
      color = '#94a3b8';
      cssClass = 'cat-normal';
    }

    // Krizės išimtis: Jei krizė, 3 pigiausios tampa "Normalu"
    if (isCrisis && cheapestHoursInCrisis.includes(h.hour)) {
      category = 'NORMAL';
      label = 'Normalu (min.)';
      color = '#94a3b8';
      cssClass = 'cat-normal';
    }

    // Negatyvios kainos visada Kritiškai pigu (pirmumas)
    if (price <= 0) {
      category = 'CRITICAL_CHEAP';
      label = 'Kritiškai pigu';
      color = '#10b981';
      cssClass = 'cat-critical-cheap';
    }

    return {
      hour: h.hour,
      price: price,
      rank: rank,
      category,
      label,
      color,
      cssClass
    };
  });

  return {
    insights,
    stats: { p25, p50, p75, isCrisis }
  };
}

function getPercentile(data, percentile) {
  if (data.length === 0) return 0;
  const index = (data.length - 1) * percentile;
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  const weight = index - lower;
  if (upper >= data.length) return data[lower];
  return data[lower] * (1 - weight) + data[upper] * weight;
}

function groupByHour(prices) {
  const hours = {};
  prices.forEach(p => {
    const date = new Date(p.datetime);
    const hourKey = date.getHours(); // 0-23
    if (!hours[hourKey]) {
      hours[hourKey] = { hour: hourKey, sum: 0, count: 0 };
    }
    hours[hourKey].sum += p.price;
    hours[hourKey].count += 1;
  });

  return Object.values(hours).map(h => ({
    hour: h.hour,
    price: h.sum / h.count // Valandos vidurkis (jei 15 min duomenys skirtingi, bet jie paprastai vienodi)
  })).sort((a, b) => a.hour - b.hour);
}
